import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { AdminAction, AdminActionType } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';

type AdminActionCreate = Omit<AdminAction, 'id' | 'timestamp' | 'expiresAt'>;

export interface PaginatedResponse<T> {
  data: T[];
  nextCursor: any;
  hasMore: boolean;
  totalCount: number;
}

const SupabaseAdminActionService = {
  async logAction(action: AdminActionCreate): Promise<void> {
    const { error } = await supabase
      .from('admin_actions')
      .insert({
        type: action.type,
        performed_by: action.performed_by,
        target_id: action.target_id,
        target_type: action.target_type,
        details: action.details,
        changes: action.changes || null,
      });

    if (error) throw error;
  },

  async getActions(page: number = 1, selectedUser: string = 'all', selectedAction: string = 'all', searchTerm: string = ''): Promise<PaginatedResponse<AdminAction & { id: string }>> {
    const { data, error } = await supabase
      .rpc('get_admin_actions', {
        p_page: page,
        p_selected_user: selectedUser,
        p_selected_action: selectedAction,
        p_search_term: searchTerm,
      });

    if (error) throw error;

    const transformedData = (data || []).map((item: any) => ({
      id: item.id,
      type: item.type as AdminActionType,
      performed_by: item.performed_by,
      target_id: item.target_id,
      target_type: item.target_type,
      details: item.details,
      changes: item.changes,
      timestamp: DateTime.fromISO(item.created_at, { zone: 'UTC' }),
      expires_at: item.expires_at ? DateTime.fromISO(item.expires_at, { zone: 'UTC' }) : undefined,
    })) as (AdminAction & { id: string })[];

    const totalCount = data && data.length > 0 ? Number(data[0].total_count) : 0;

    return {
      data: transformedData,
      nextCursor: transformedData[transformedData.length - 1],
      hasMore: totalCount > page * 10,
      totalCount,
    };
  },

  async getAllActions(): Promise<(AdminAction & { id: string })[]> {
    const { data, error } = await supabase
      .from('admin_actions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((item: any) => ({
      id: item.id,
      type: item.type as AdminActionType,
      performed_by: item.performed_by,
      target_id: item.target_id,
      target_type: item.target_type,
      details: item.details,
      changes: item.changes,
      timestamp: DateTime.fromISO(item.created_at, { zone: 'UTC' }),
      expires_at: item.expires_at ? DateTime.fromISO(item.expires_at, { zone: 'UTC' }) : undefined,
    })) as (AdminAction & { id: string })[];
  },
};

export const useAdminActions = (page: number = 1, selectedUser: string = 'all', selectedAction: string = 'all', searchTerm: string = '') => {
  return useQuery<PaginatedResponse<AdminAction & { id: string }>>({
    queryKey: ['supabase-adminActions', page, selectedUser, selectedAction, searchTerm],
    queryFn: () => SupabaseAdminActionService.getActions(page, selectedUser, selectedAction, searchTerm),
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 30,
  });
};

export const useAllAdminActions = () => {
  return useQuery({
    queryKey: ['supabase-allAdminActions'],
    queryFn: () => SupabaseAdminActionService.getAllActions(),
    gcTime: 1000 * 60 * 30,
  });
};

export const useLogAdminAction = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (action: AdminActionCreate) => 
      SupabaseAdminActionService.logAction(action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-adminActions'] });
      queryClient.invalidateQueries({ queryKey: ['supabase-allAdminActions'] });
    },
  });
};

export default SupabaseAdminActionService;